import React, { useEffect, useState } from "react";
import { X, ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIPPING_FLAT_RATE = 8.5;
const MIN_QUANTITY = 1;
const MAX_QUANTITY = 99;

const parsePrice = (price) => {
  if (typeof price === "number") return price;
  if (typeof price === "string") {
    const n = parseFloat(price.replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const businessDaysFromNow = (days) => {
  const d = new Date();
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return d;
};

const formatDate = (date) =>
  date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const initialForm = {
  customer_name: "",
  email: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "United States",
  notes: "",
};

/* ---------- sub-components ---------- */

const OrderSummary = ({ quantity, unitPrice, subtotal, shipping, total, estFrom, estTo }) => (
  <div className="order-summary" data-testid="order-summary">
    <div className="order-summary-line">
      <span>Order amount</span>
      <span>{quantity} × ${unitPrice.toFixed(2)}</span>
    </div>
    <div className="order-summary-line">
      <span>Subtotal</span>
      <span>${subtotal.toFixed(2)}</span>
    </div>
    <div className="order-summary-line">
      <span>Estimated shipping</span>
      <span>${shipping.toFixed(2)}</span>
    </div>
    <div className="order-summary-line order-summary-total">
      <span>Order total</span>
      <span>${total.toFixed(2)}</span>
    </div>
    <div className="order-summary-line order-summary-delivery">
      <span>Estimated delivery</span>
      <span>{estFrom} – {estTo}</span>
    </div>
  </div>
);

const Field = ({ label, name, type = "text", value, onChange, autoComplete, required, testid, min, max }) => (
  <label className="order-field">
    <span>{label}</span>
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      required={required}
      min={min}
      max={max}
      data-testid={testid}
    />
  </label>
);

const OrderForm = ({ product, unitPrice, quantity, setQuantity, form, onChange, onSubmit, submitting, error }) => {
  const subtotal = +(unitPrice * quantity).toFixed(2);
  const total = +(subtotal + SHIPPING_FLAT_RATE).toFixed(2);
  const estFrom = formatDate(businessDaysFromNow(5));
  const estTo = formatDate(businessDaysFromNow(7));

  return (
    <>
      <p className="order-eyebrow">Complete your order</p>
      <h3 id="order-modal-title" className="order-title">{product?.name}</h3>
      <p className="order-subtitle">
        ${unitPrice.toFixed(2)} · Handcrafted in Hawaiʻi
      </p>

      <form onSubmit={onSubmit} className="order-form" data-testid="order-form">
        <div className="order-grid-2">
          <Field label="Full name"   name="customer_name" value={form.customer_name} onChange={onChange} autoComplete="name"  required testid="order-field-name" />
          <Field label="Email"       name="email"         type="email" value={form.email} onChange={onChange} autoComplete="email" required testid="order-field-email" />
        </div>

        <div className="order-grid-2">
          <Field label="Cell / phone" name="phone" type="tel" value={form.phone} onChange={onChange} autoComplete="tel" required testid="order-field-phone" />
          <label className="order-field">
            <span>Quantity</span>
            <input
              type="number" min={MIN_QUANTITY} max={MAX_QUANTITY}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(MIN_QUANTITY, Math.min(MAX_QUANTITY, parseInt(e.target.value, 10) || MIN_QUANTITY)))}
              required
              data-testid="order-field-quantity"
            />
          </label>
        </div>

        <Field label="Street address" name="address_line1" value={form.address_line1} onChange={onChange} autoComplete="address-line1" required testid="order-field-address1" />
        <Field label="Apt, suite, etc. (optional)" name="address_line2" value={form.address_line2} onChange={onChange} autoComplete="address-line2" />

        <div className="order-grid-3">
          <Field label="City"  name="city"        value={form.city}        onChange={onChange} autoComplete="address-level2" required testid="order-field-city" />
          <Field label="State" name="state"       value={form.state}       onChange={onChange} autoComplete="address-level1" required testid="order-field-state" />
          <Field label="ZIP"   name="postal_code" value={form.postal_code} onChange={onChange} autoComplete="postal-code"    required testid="order-field-zip" />
        </div>

        <label className="order-field">
          <span>Order notes (optional)</span>
          <textarea name="notes" rows="2" value={form.notes} onChange={onChange} />
        </label>

        <OrderSummary
          quantity={quantity} unitPrice={unitPrice} subtotal={subtotal}
          shipping={SHIPPING_FLAT_RATE} total={total} estFrom={estFrom} estTo={estTo}
        />

        {error && <p className="order-error" role="alert">{error}</p>}

        <button
          type="submit"
          className="btn-primary order-submit"
          disabled={submitting}
          data-testid="order-submit"
        >
          {submitting ? "Placing order…" : <>Confirm Order <ArrowRight size={14} /></>}
        </button>
        <p className="order-fine-print">
          No card is charged here. We’ll email you to finalise payment &amp; shipping details.
        </p>
      </form>
    </>
  );
};

const OrderConfirmation = ({ confirmation, customerEmail, onClose }) => (
  <div className="order-confirmation" data-testid="order-confirmation">
    <CheckCircle2 size={36} className="order-confirmation-icon" aria-hidden="true" />
    <p className="order-eyebrow">Order received</p>
    <h3 id="order-modal-title" className="order-title">Mahalo, your order is confirmed.</h3>
    <p className="order-confirmation-sub">
      Order <strong>{confirmation.order_number}</strong> — a summary has been
      saved. We’ll reach out to <strong>{customerEmail || "you"}</strong> shortly
      with payment &amp; shipping details.
    </p>

    <dl className="order-summary-confirm">
      <div><dt>Item</dt><dd>{confirmation.product_name} × {confirmation.quantity}</dd></div>
      <div><dt>Subtotal</dt><dd>${confirmation.subtotal.toFixed(2)}</dd></div>
      <div><dt>Shipping</dt><dd>${confirmation.shipping_fee.toFixed(2)}</dd></div>
      <div className="order-summary-total"><dt>Total</dt><dd>${confirmation.total.toFixed(2)}</dd></div>
      <div>
        <dt>Estimated delivery</dt>
        <dd>{formatDate(new Date(confirmation.estimated_delivery_from))} – {formatDate(new Date(confirmation.estimated_delivery_to))}</dd>
      </div>
    </dl>

    <button type="button" className="btn-primary" onClick={onClose} data-testid="order-done">
      Done
    </button>
  </div>
);

/* ---------- main shell ---------- */

const OrderModal = ({ product, onClose }) => {
  const [form, setForm] = useState(initialForm);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState("");

  const unitPrice = parsePrice(product?.price);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/orders/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: product.id || product.product_id || "unknown",
          product_name: product.name,
          unit_price: unitPrice,
          quantity,
          ...form,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Unable to place order");
      }
      const data = await res.json();
      setConfirmation(data);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="order-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-modal-title"
      onClick={onClose}
      data-testid="order-modal"
    >
      <div className="order-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="order-modal-close"
          aria-label="Close order form"
          onClick={onClose}
          data-testid="order-modal-close"
        >
          <X size={20} aria-hidden="true" />
        </button>

        {confirmation ? (
          <OrderConfirmation
            confirmation={confirmation}
            customerEmail={form.email}
            onClose={onClose}
          />
        ) : (
          <OrderForm
            product={product}
            unitPrice={unitPrice}
            quantity={quantity}
            setQuantity={setQuantity}
            form={form}
            onChange={change}
            onSubmit={submit}
            submitting={submitting}
            error={error}
          />
        )}
      </div>
    </div>
  );
};

export default OrderModal;
